import React, { useState } from 'react';
import { NEW_SITE_BASE_INFO_INPUTS } from '../../../constants/sites/site-base-information';
import { useTranslation } from 'react-i18next';
import { FormInput } from '../FormInput';
import { useFormik } from 'formik';
import {
	convertToInitial,
	EditSiteValues,
	FORM_FIELDS,
	getSitesDetailsSchema,
	initialValues, SiteTypes
} from '../../../constants/sites/site-form';
import { NEW_SITE_CUSTOMER_INFO_INPUTS } from '../../../constants/sites/site-custome-service-information';
import { ButtonComponent } from '../../ButtonComponent';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import './AddNewSite.scss';
import { addSite, editSite, fetchMerchantSites } from '../../../store/merchnts/actions/sites';
import { ISitesProps } from '../../../views/merchants/details/MerchantSitesPage';
import clsx from 'clsx';
import { UrlList } from './UrlList';
import { Title } from '../../developers/page-components/Title';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import { TagList } from './TagList';

interface Props {
	merchantId: string;
	closeModal?: () => void;
	chosenSite?: ISitesProps;
}

export const AddNewSite = (props: Props): React.ReactElement => {
	const { merchantId, closeModal, chosenSite } = props;
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const isAdmin = checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN);
	const isMerchant = checkRoles(authUser, CLAIMS.MERCHANT_ADMIN);
	const isIntegrationPartner = checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN);
	const { t } = useTranslation();
	const { dispatch } = useMerchants();
	const formik = useFormik<SiteTypes>({
		initialValues: chosenSite
			? EditSiteValues(chosenSite)
			: initialValues,
		validationSchema: getSitesDetailsSchema(t),
		onSubmit: () => addNewSite()
	});
	const [loader, setLoader] = useState<boolean>(false);
	const [whitelistedUrls, setWhitelistedUrls] = useState<string[]>(formik.values[FORM_FIELDS.WHITELISTED_URLS]);
	const [tags, setTags] = useState<string[]>(formik.values.Tags);

	const addNewSite = async (): Promise<void> => {
		if (!formik.isValid) return;
		setLoader(true);
		try {
			if (chosenSite?.id) {
				const data = convertToInitial(formik.values);
				const params = {
					id: chosenSite.id,
					...data,
					...formik.values,
				};
				await editSite(merchantId, chosenSite.id, params, formik);
			} else {
				await addSite(merchantId, formik.values, formik);
			}
			setLoader(false);
			if (!isMerchant) {
				closeModal();
				await fetchMerchantSites(dispatch, merchantId);
			}
		} catch (e) {
			console.log('AddNewSite error', e);
			setLoader(false);
		}

	};

	const link = formik.getFieldProps(FORM_FIELDS.URL).value;

	const BASE_INPUTS = NEW_SITE_BASE_INFO_INPUTS({ t, isAdmin });
	const CUSTOMER_INFO_INPUTS = NEW_SITE_CUSTOMER_INFO_INPUTS({ t, link });
	const tagsList = formik.getFieldProps(FORM_FIELDS.TAGS).value;


	return (
		<form className={clsx('row', (isAdmin || isIntegrationPartner) && 'px-8 pb-8')} onSubmit={formik.handleSubmit}>
			{
				BASE_INPUTS.map(item => (
					<FormInput
						{...item}
						formik={formik}
						key={item.key}
						isEditMode={isAdmin}
					/>
				))
			}
			<div className="fs-6 fw-bolder my-5">
				Customer service information
			</div>
			{
				CUSTOMER_INFO_INPUTS.map(item => (
					<FormInput
						{...item}
						formik={formik}
						key={item.key}
						isEditMode={isAdmin}
					/>
				))
			}

			<UrlList
				title='Whitelisted URLs'
				formik={formik}
				urls={whitelistedUrls}
				setUrls={setWhitelistedUrls}
				isAdmin={isAdmin}
				fieldName={FORM_FIELDS.WHITELISTED_URLS}
				label='URL'
				isEditMode={isAdmin}
			/>
			{
				 isAdmin || tagsList.length
				 ? (
						<TagList
							title='Site tags'
							formik={formik}
							tags={tags}
							setTags={setTags}
							isAdmin={isAdmin}
							fieldName={FORM_FIELDS.TAGS}
							label='tag'
							isEditMode={isAdmin}
						/>
					) : (
						<>
							<Title title='Site tags' additionalClass='fs-6 fw-bolder my-5'/>
							<div>No site tags</div>
						</>
					)
			}
			{
				isAdmin && (
					<ButtonComponent
						buttonName={chosenSite?.id ? 'Save' : 'Add'}
						type="submit"
						additionalClass="d-block ms-auto w-85px me-3 mt-5"
						loading={loader}
						isDisabled={loader}
					/>
				)
			}
		</form>
	);
};
